import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type Theme = "light" | "dark";
const STORAGE_KEY = "navioplan.theme";

// Default to light until every page's colours are token-migrated — a dark shell over light pages would look
// broken. Once pages are migrated we can fall back to the OS preference here.
function initialTheme(): Theme {
  const stored = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
  return stored === "dark" ? "dark" : "light";
}

const ThemeContext = createContext<{ theme: Theme; toggleTheme: () => void }>({ theme: "light", toggleTheme: () => {} });

// Flips the `dark` class on <html> (the @navio-maritime/ui token layer + Tailwind `darkMode: ['class']` read
// it) and remembers the choice. Mounted above the whole app so login is themed too.
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);
  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
